import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { activeSessionInvocationsQueryKey } from "@/shared/domain/actions/hooks";
import { invocationSessionCondition } from "@/shared/domain/actions/invocationSearchConditions";

import { cancelUntilDone } from "./cancelUntilDone";

export interface CancelInvocationsBySessionParams {
  sessionId: string;
}

/**
 * Cancel the active invocations whose input names a session, batch by batch until none remain.
 */
export function useCancelInvocationsBySession() {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<undefined, Error, CancelInvocationsBySessionParams>({
    mutationFn: async ({ sessionId }) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("Requires login"));
      }

      await cancelUntilDone(() =>
        actionService.cancelActiveInvocations(
          currentOrganization.org_id,
          invocationSessionCondition(sessionId),
        ),
      );
    },
    onSuccess: (_, { sessionId }) => {
      void queryClient.invalidateQueries({
        queryKey: activeSessionInvocationsQueryKey(sessionId),
      });
      void queryClient.invalidateQueries({ queryKey: ["invocationSearch"] });
    },
  });
}
